/**
 * Shipping & Payment Info — Payment Icons Infinite Carousel
 * assets/shipping-payment-info.js
 *
 * – Solo activa clones y autoplay cuando los iconos originales exceden el ancho.
 * – Transform: translateX() GPU-accelerated para scroll continuo y fluido.
 * – Pausa en interacción táctil/mouse y reanuda.
 * – En resize limpia/recrea según necesidad.
 */

(function () {
  'use strict';

  const SPEED = 35; // px/s
  const BUFFER = 20;

  function initPaymentCarousel(section) {
    const carousel = section.querySelector('.spi-payment__carousel');
    if (!carousel) return;
    if (carousel.dataset.spiInitialized === 'true') return;
    carousel.dataset.spiInitialized = 'true';

    const icons = Array.from(carousel.querySelectorAll('.spi-payment__icon'));
    if (icons.length < 2) return;

    // Guardar HTML original para poder restaurar
    const originalHTML = carousel.innerHTML;

    let rafId = null;
    let lastTime = 0;
    let isPaused = false;
    let resumeTimer = null;
    let wrapper = null;
    let setWidth = 0;
    let translateX = 0;
    let isInfiniteActive = false;

    // ── Helpers ────────────────────────────────────────────────────
    const hasOverflow = () => {
      // Medir con los iconos actuales del DOM (sin clones)
      const currentIcons = carousel.querySelectorAll('.spi-payment__icon');
      if (currentIcons.length < 2) return false;
      const r1 = currentIcons[0].getBoundingClientRect();
      const r2 = currentIcons[1].getBoundingClientRect();
      const singleWidth = r2.left - r1.left;
      const totalWidth = singleWidth * icons.length;
      return totalWidth > carousel.clientWidth + 2;
    };

    const applyTransform = () => {
      if (wrapper) {
        wrapper.style.transform = `translateX(${-translateX}px)`;
      }
    };

    // ── Construir carrusel infinito ────────────────────────────────
    const buildInfinite = () => {
      if (isInfiniteActive) return;

      wrapper = document.createElement('div');
      wrapper.classList.add('spi-payment__track');
      wrapper.style.display = 'flex';
      wrapper.style.gap = getComputedStyle(carousel).gap || '1rem';
      wrapper.style.alignItems = 'center';
      wrapper.style.willChange = 'transform';

      // Mover iconos actuales al wrapper
      while (carousel.firstChild) {
        wrapper.appendChild(carousel.firstChild);
      }
      carousel.appendChild(wrapper);
      carousel.style.display = 'block';
      carousel.style.overflow = 'hidden';

      // Clonar usando los iconos originales
      const originalIcons = Array.from(wrapper.querySelectorAll('.spi-payment__icon'))
        .filter(el => !el.classList.contains('spi-payment__icon--clone'));

      const cloneFrom = (list) =>
        list.map(icon => {
          const clone = icon.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('tabindex', '-1');
          clone.classList.add('spi-payment__icon--clone');
          return clone;
        });

      cloneFrom(originalIcons).reverse().forEach(c => wrapper.insertBefore(c, wrapper.firstChild));
      cloneFrom(originalIcons).forEach(c => wrapper.appendChild(c));

      // Calcular ancho del set
      const all = Array.from(wrapper.querySelectorAll('.spi-payment__icon'));
      if (all.length >= 2) {
        const r1 = all[0].getBoundingClientRect();
        const r2 = all[1].getBoundingClientRect();
        setWidth = (r2.left - r1.left) * originalIcons.length;
      }

      translateX = setWidth;
      applyTransform();
      isInfiniteActive = true;
    };

    // ── Destruir carrusel infinito ─────────────────────────────────
    const destroyInfinite = () => {
      stopLoop();
      if (!isInfiniteActive) return;

      carousel.innerHTML = originalHTML;
      carousel.style.display = '';
      carousel.style.overflow = '';
      wrapper = null;
      setWidth = 0;
      translateX = 0;
      isInfiniteActive = false;
    };

    // ── RAF Autoplay ───────────────────────────────────────────────
    const tick = (timestamp) => {
      if (!isPaused && isInfiniteActive && setWidth > 0) {
        const elapsed = lastTime ? (timestamp - lastTime) / 1000 : 0;
        if (elapsed > 0 && elapsed < 0.5) {
          translateX += SPEED * elapsed;
        }

        // Teleport infinito
        if (wrapper) {
          const maxTranslate = wrapper.scrollWidth - setWidth;
          if (translateX < BUFFER) {
            translateX += setWidth;
          } else if (translateX > maxTranslate - BUFFER) {
            translateX -= setWidth;
          }
          applyTransform();
        }
      }

      lastTime = timestamp;
      rafId = requestAnimationFrame(tick);
    };

    const startAutoplay = () => {
      isPaused = false;
      lastTime = 0;
      if (!rafId) {
        rafId = requestAnimationFrame(tick);
      }
    };

    const pauseAutoplay = () => {
      isPaused = true;
      lastTime = 0;
    };

    const stopLoop = () => {
      pauseAutoplay();
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // ── Inicializar según overflow ─────────────────────────────────
    if (hasOverflow()) {
      buildInfinite();
      startAutoplay();
    }
    // Si no hay overflow: el carrusel mantiene su layout flex nativo, sin cambios

    // ── Interacción ────────────────────────────────────────────────
    carousel.addEventListener('touchstart', () => {
      pauseAutoplay();
      clearTimeout(resumeTimer);
    }, { passive: true });

    carousel.addEventListener('mousedown', () => {
      pauseAutoplay();
      clearTimeout(resumeTimer);
    }, { passive: true });

    const resume = () => {
      resumeTimer = setTimeout(() => {
        if (isInfiniteActive) startAutoplay();
      }, 1500);
    };

    carousel.addEventListener('touchend', resume, { passive: true });
    carousel.addEventListener('mouseup', resume, { passive: true });

    // ── Resize ─────────────────────────────────────────────────────
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      pauseAutoplay();

      resizeTimer = setTimeout(() => {
        const needsOverflow = hasOverflow();

        if (needsOverflow) {
          if (!isInfiniteActive) {
            buildInfinite();
          }
          setWidth = (() => {
            const all = Array.from(wrapper.querySelectorAll('.spi-payment__icon'))
              .filter(el => !el.classList.contains('spi-payment__icon--clone'));
            if (all.length < 2) return 0;
            const r1 = all[0].getBoundingClientRect();
            const r2 = all[1].getBoundingClientRect();
            return (r2.left - r1.left) * icons.length;
          })();
          if (translateX < 0) translateX = setWidth;
          applyTransform();
          startAutoplay();
        } else {
          if (isInfiniteActive) {
            destroyInfinite();
          }
        }
      }, 250);
    }, { passive: true });
  }

  function init() {
    document.querySelectorAll('.shipping-payment-info-section').forEach(initPaymentCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();